/**
 * Startseite und Menüpunkt „eBay & Live-Update“: Zustand der eBay-Verbindung, Live-Update, Zähler und Abgleich (Aufträge aus `JOB#`).
 * Zeigt nur, was der Worker gemeldet hat; fehlende Zähler bleiben „–“. Inhaber sehen oben einen Hinweis auf
 * offene Rechnungen wie im Altsystem.
 */
import { Alert, Anchor, Badge, Button, Card, Group, SimpleGrid, Stack, Table, Text, ThemeIcon, Title, type AnchorProps } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createLink } from '@tanstack/react-router';
import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from 'react';
import type { StatusResponse } from '../../../shared/contract.js';
import { count, cutoffDate, dateOnly, dateTime, has, t, type TextKey } from '../texts.js';
import { useFrozen, useServices } from '../services.js';
import { siteLabel } from '../sites.js';
import { ApiRequestError } from '../api/client.js';
import { EbayConnectButton, ResumeSync } from './ebay-pages.js';
import { LiveUpdateCard, LiveUpdateItemCard, LiveUpdatePreview } from './live-update-page.js';
import { FullPageLoader, QueryErrorState, useMe } from './tenant-layout.js';

const EBAY_COLORS: Record<StatusResponse['ebay']['state'], string> = {
  ok: 'green',
  expiring: 'yellow',
  expired: 'red',
  invalid: 'red',
  missing: 'gray',
};

const JOB_COLORS = { idle: 'gray', running: 'blue', failed: 'red', paused: 'yellow' } as const;

const RouterAnchor = createLink(
  forwardRef<HTMLAnchorElement, AnchorProps & AnchorHTMLAttributes<HTMLAnchorElement>>(function LinkAnchor(props, ref) {
    return <Anchor component="a" ref={ref} {...props} />;
  }),
);

/**
 * Hinweis auf offene Rechnungen (nur Inhaber, nicht im Nur-Rechnungen-Zugang). Ein Lesefehler zeigt nichts: Der
 * Hinweis ist eine Erinnerung, die Rechnungen selbst stehen auf der Kontoseite.
 */
function OpenInvoicesNotice({ tenantId }: { tenantId: string }) {
  const { api } = useServices();
  const membership = useMe().data?.memberships.find(m => m.tenantId === tenantId);
  const frozen = useFrozen();
  const enabled = !frozen && membership?.role === 'owner' && membership.invoicesOnly !== true;
  const query = useQuery({ queryKey: ['invoices-open', tenantId], queryFn: () => api.openInvoices(tenantId), enabled, retry: false, staleTime: 300_000 });
  const open = query.data?.open ?? 0;
  if (!enabled || open === 0) return null;
  return (
    <Alert color="yellow" title={t(open === 1 ? 'status.openInvoices.one' : 'status.openInvoices.other', { count: count(open) })}>
      <RouterAnchor to="/t/$tenantId/account" params={{ tenantId }} size="sm">
        {t('status.openInvoices.link')}
      </RouterAnchor>
    </Alert>
  );
}

export function useStatus(tenantId: string) {
  const { api } = useServices();
  return useQuery({ queryKey: ['status', tenantId], queryFn: () => api.status(tenantId), refetchInterval: 60_000 });
}

export function Fact({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" gap="xs" wrap="nowrap">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text size="sm" ta="right">
        {value}
      </Text>
    </Group>
  );
}

/** eBay-Verbindung; auch auf der Kontoseite. Neu verbinden nur für Inhaber (der Server prüft trotzdem). */
export function EbayCard({ status }: { status: StatusResponse }) {
  const { ebay } = status;
  const needsAction = ebay.state !== 'ok';
  const membership = useMe().data?.memberships.find(m => m.tenantId === status.tenantId);
  const role = membership?.role;
  // BETA: die API meldet `connectEbay` nur, wo die Token-Erneuerung eingerichtet ist (Flori 31.10.2026); sonst eingefroren.
  const canConnect = status.capabilities.connectEbay;
  const frozen = useFrozen() && membership?.beta !== true;
  return (
    <Card withBorder padding="lg">
      <Stack gap="xs">
        <Group justify="space-between">
          <Title order={4}>{t('status.ebay')}</Title>
          <Badge color={EBAY_COLORS[ebay.state]}>{t(`status.ebay.${ebay.state}`)}</Badge>
        </Group>
        {ebay.userName && <Fact label={t('status.ebay.user')} value={ebay.userName} />}
        {ebay.primarySiteId !== undefined && <Fact label={t('items.site')} value={siteLabel(ebay.primarySiteId)} />}
        {ebay.expiresAt && (
          <Fact
            label={t('status.ebay.expires')}
            value={`${ebay.expiresAtEstimated ? 'ca. ' : ''}${dateTime(ebay.expiresAt)}${ebay.daysLeft !== undefined && ebay.daysLeft >= 0 ? ` (${t('status.ebay.daysLeft', { days: ebay.daysLeft })})` : ''}`}
          />
        )}
        {ebay.expiresAtEstimated && (
          <Text size="xs" c="dimmed">
            {t('status.ebay.estimated')}
          </Text>
        )}
        {needsAction && (
          <Alert color={EBAY_COLORS[ebay.state]} variant="light">
            {t('status.ebay.reconnectHint')}
          </Alert>
        )}
        {!frozen && canConnect && role === 'owner' && <EbayConnectButton tenantId={status.tenantId} firstConnect={ebay.state === 'missing'} prominent={needsAction} />}
        {!frozen && canConnect && role !== 'owner' && needsAction && (
          <Text size="xs" c="dimmed">
            {t('ebay.connectOwnerOnly')}
          </Text>
        )}
      </Stack>
    </Card>
  );
}

/** Pause des Abgleichs und ausstehende Wiederaufnahme; sonst nichts (Zeiten stehen in der Auftragstabelle). */
function SyncNotice({ status }: { status: StatusResponse }) {
  const { sync } = status;
  const frozen = useFrozen();
  const role = useMe().data?.memberships.find(m => m.tenantId === status.tenantId)?.role;
  if (frozen || (!sync.pauseReason && !sync.resumePending)) return null;
  return (
    <Stack gap="xs">
      {sync.pauseReason && (
        <PauseNotice status={status} blocked={role !== 'owner' ? 'owner' : status.migrationState !== 'migriert' ? 'migration' : undefined} />
      )}
      {sync.resumePending && <ResumeSync tenantId={status.tenantId} canResume={role === 'owner'} />}
    </Stack>
  );
}

/**
 * Warum der Abgleich pausiert und wie es weitergeht (Festlegung 24.10.2026): Token ungültig → neu verbinden
 * (eBay-Karte), Limit → setzt der Worker ab `resumeAfter` selbst fort, manuell → Inhaber hebt sie hier auf, erst ab
 * `migriert` (Entscheidung Claude 25.10.2026; vorher hält der Umzug die Pause). `blocked`: warum kein Knopf.
 */
function PauseNotice({ status, blocked }: { status: StatusResponse; blocked: 'owner' | 'migration' | undefined }) {
  const { api } = useServices();
  const queryClient = useQueryClient();
  const { sync } = status;
  const lift = useMutation({
    mutationFn: () => api.liftPause(status.tenantId, sync.pausedAt!),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['status', status.tenantId] }),
  });
  const since = sync.pausedAt ? ` ${t('status.pause.since', { at: dateTime(sync.pausedAt) })}` : '';
  if (sync.pauseReason === 'token-invalid') return <Text size="sm" c="red">{t('status.pause.token-invalid') + since}</Text>;
  if (sync.pauseReason === 'rate-limit') {
    return (
      <Text size="sm" c="dimmed">
        {(sync.resumeAfter ? t('status.pause.rate-limit.until', { at: dateTime(sync.resumeAfter) }) : t('status.pause.rate-limit')) + since}
      </Text>
    );
  }
  return (
    <Stack gap={4}>
      <Text size="sm">{t('status.pause.manual') + since}</Text>
      {lift.isError && (
        <Text size="sm" c="red" role="alert">
          {lift.error instanceof ApiRequestError && lift.error.detail ? lift.error.detail : t('common.error')}
        </Text>
      )}
      {blocked === undefined && sync.pausedAt ? (
        <Button size="xs" w="fit-content" variant="light" loading={lift.isPending} onClick={() => lift.mutate()}>
          {t('status.pause.lift')}
        </Button>
      ) : (
        <Text size="xs" c="dimmed">
          {t(blocked === 'migration' ? 'status.pause.migration' : 'status.pause.ownerOnly')}
        </Text>
      )}
    </Stack>
  );
}

function CountCard({ label, value, alert }: { label: string; value: number | null; alert?: boolean }) {
  return (
    <Card withBorder padding="lg">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text fz={32} fw={700} {...(alert && value ? { c: 'red' } : {})}>
        {value === null ? t('common.unknown') : count(value)}
      </Text>
    </Card>
  );
}

/** Zähler des letzten Laufs, z. B. „geprüft 12 · vorgemerkt 3“; nur bekannte Schlüssel (deutsche Texte). */
function countersText(counters: Record<string, number> | undefined): string | undefined {
  const entries = Object.entries(counters ?? {}).filter(([key]) => has(`job.counter.${key}`));
  if (entries.length === 0) return undefined;
  return entries.map(([key, value]) => `${t(`job.counter.${key}` as TextKey)} ${count(value)}`).join(' · ');
}

/** Bekannte Fehlercodes der Worker lesbar, sonst die Meldung des Workers (heute meist der Code selbst). */
function jobErrorText(error: { code: string; message: string }): string {
  const key = `job.error.${error.code}`;
  return has(key) ? t(key) : error.message;
}

function JobsTable({ status }: { status: StatusResponse }) {
  const { api } = useServices(), client = useQueryClient();
  const role = useMe().data?.memberships.find(row => row.tenantId === status.tenantId)?.role;
  const canRequest = role === 'owner' || role === 'editor', demand = status.storeSyncRequest;
  const refresh = useMutation({ mutationFn: () => api.requestStoreSync(status.tenantId),
    onSuccess: saved => client.setQueryData<StatusResponse>(['status', status.tenantId], current => current ? { ...current, storeSyncRequest: saved } : current),
    onSettled: () => { void client.invalidateQueries({ queryKey: ['status', status.tenantId] }); } });
  const jobs: StatusResponse['jobs'] = (demand?.available || demand?.pending) && !status.jobs.some(job => job.type === 'store.sync')
    ? [...status.jobs, { type: 'store.sync' as const, state: 'idle' as const }] : status.jobs;
  if (jobs.length === 0) return <Text c="dimmed">{t('status.job.none')}</Text>;
  return (
    <Stack gap="xs">
      {refresh.isError && <Text size="sm" c="red">{t('status.storeRefresh.error')}</Text>}
      <Table striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t('status.job.type')}</Table.Th>
            <Table.Th>{t('status.job.state')}</Table.Th>
            <Table.Th>{t('status.job.lastSuccess')}</Table.Th>
            <Table.Th>{t('status.job.next')}</Table.Th>
            <Table.Th>{t('status.job.error')}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {jobs.map(job => {
            const label = `job.${job.type}`;
            return (
              <Table.Tr key={job.type}>
                <Table.Td>
                  {has(label) ? t(label) : job.type}
                  {job.type === 'store.sync' && demand && <Stack gap={4} mt={4}>
                    {canRequest && <Button size="xs" variant="light" loading={refresh.isPending}
                      disabled={!demand.available || demand.pending}
                      title={!demand.available ? t('status.storeRefresh.unavailable') : undefined}
                      onClick={() => refresh.mutate()}>{t('status.storeRefresh.request')}</Button>}
                    {demand.pending && <Text size="xs" c="dimmed">{t('status.storeRefresh.pending')}</Text>}
                  </Stack>}
                </Table.Td>
                <Table.Td>
                  <Badge color={JOB_COLORS[job.state]} variant="light">
                    {t(`jobState.${job.state}`)}
                  </Badge>
                  {countersText(job.counters) && (
                    <Text size="xs" c="dimmed">
                      {countersText(job.counters)}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>{job.lastSuccessAt ? dateTime(job.lastSuccessAt) : t('common.never')}</Table.Td>
                {/* `nextRunAt` aus `JOB#`; die Lese-Jobs (Änderungen holen, Abgleich, Shopkategorien) schreiben ihn noch nicht. */}
                <Table.Td>{dateTime(job.nextRunAt)}</Table.Td>
                <Table.Td>
                  {job.lastError ? (
                    <Text size="sm" c="red">
                      {dateTime(job.lastError.at)}: {jobErrorText(job.lastError)}
                    </Text>
                  ) : (
                    t('common.unknown')
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}

/** Kachel der Übersicht: Titel mit Zustand, kurzer Inhalt, unten ein Verweis auf die zuständige Seite. */
function Tile({ title, badge, children, link }: { title: string; badge?: ReactNode; children: ReactNode; link?: ReactNode }) {
  return (
    <Card withBorder padding="lg" h="100%">
      <Stack gap="xs" h="100%">
        <Group justify="space-between" wrap="nowrap">
          <Title order={4}>{title}</Title>
          {badge}
        </Group>
        <Stack gap={4} style={{ flex: 1 }}>
          {children}
        </Stack>
        {link}
      </Stack>
    </Card>
  );
}

/**
 * Übersicht (Flori 01.11.2026, für alle Konten gleich): Zustand von eBay-Verbindung, Live-Update und Shopkategorien als
 * Kacheln mit Verweis auf ihre Seite, darunter der Weg zur Artikelbeschreibung und – solange Teile fehlen – woran wir
 * arbeiten. Ersetzt die BETA-Hinweise; Einzelheiten und Abgleich stehen auf „eBay & Live-Update“.
 */
export function StartPage({ tenantId }: { tenantId: string }) {
  const status = useStatus(tenantId);
  const membership = useMe().data?.memberships.find(m => m.tenantId === tenantId);
  if (status.isPending) return <FullPageLoader />;
  if (status.isError) return <QueryErrorState error={status.error} onRetry={() => void status.refetch()} />;
  const data = status.data;
  const importing = data.jobs.some(j => j.type === 'tenant.import' && j.state === 'running');
  return (
    <Stack gap="lg">
      {/* Ohne Seitentitel (Flori 01.11.2026): der Kontoname aus dem Import ist nicht überall sauber; das Konto steht oben. */}
      <OpenInvoicesNotice tenantId={tenantId} />
      {data.accountStatus === 'cancel-scheduled' && data.cancelEffectiveAt && (
        <Alert color="yellow">{t('status.cancelScheduled', { date: cutoffDate(data.cancelEffectiveAt) })}</Alert>
      )}
      {importing && <Alert color="blue">{t('start.import.running')}</Alert>}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }}>
        <EbayTile status={data} />
        <LiveUpdateTile status={data} />
        <StoreTile status={data} />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, md: membership?.beta ? 2 : 1 }}>
        <StepsCard tenantId={tenantId} />
        {membership?.beta && <NextCard />}
      </SimpleGrid>
    </Stack>
  );
}

function EbayTile({ status }: { status: StatusResponse }) {
  const { ebay } = status;
  return (
    <Tile
      title={t('status.ebay')}
      badge={<Badge color={EBAY_COLORS[ebay.state]}>{t(`status.ebay.${ebay.state}`)}</Badge>}
      link={
        <RouterAnchor to="/t/$tenantId/ebay" params={{ tenantId: status.tenantId }} size="sm" fw={500}>
          {ebay.state === 'ok' ? t('start.manage') : t('ebay.connect')}
        </RouterAnchor>
      }
    >
      {ebay.userName && <Fact label={t('status.ebay.user')} value={ebay.userName} />}
      {ebay.expiresAt && <Fact label={t('status.ebay.expires')} value={`${ebay.expiresAtEstimated ? 'ca. ' : ''}${dateOnly(ebay.expiresAt)}`} />}
      {ebay.state !== 'ok' && (
        <Text size="sm" c={EBAY_COLORS[ebay.state]}>
          {t('status.ebay.reconnectHint')}
        </Text>
      )}
    </Tile>
  );
}

function LiveUpdateTile({ status }: { status: StatusResponse }) {
  const { liveUpdate } = status;
  return (
    <Tile
      title={t('liveUpdate.title')}
      badge={<Badge color={liveUpdate.enabled ? 'green' : 'gray'}>{t(liveUpdate.enabled ? 'liveUpdate.badge.on' : 'liveUpdate.badge.off')}</Badge>}
      link={
        <RouterAnchor to="/t/$tenantId/ebay" params={{ tenantId: status.tenantId }} size="sm" fw={500}>
          {liveUpdate.enabled || !liveUpdate.allowed ? t('start.manage') : t('start.liveUpdate.switchOn')}
        </RouterAnchor>
      }
    >
      <Text size="sm" c="dimmed">
        {!liveUpdate.allowed ? t('liveUpdate.notAllowed') : t(liveUpdate.enabled ? 'start.liveUpdate.on' : 'start.liveUpdate.off')}
      </Text>
    </Tile>
  );
}

/** Shopkategorien (bei übernommenen Konten aus dem bisherigen PAT, sonst aus dem Store-Lauf). */
function StoreTile({ status }: { status: StatusResponse }) {
  const store = status.store;
  return (
    <Tile title={t('start.store.title')}>
      {store ? (
        <>
          <Fact label={t('start.store.count')} value={count(store.categoryCount)} />
          <Fact label={t('start.store.asOf')} value={dateTime(store.fetchedAt)} />
          {store.subscription !== 'subscribed' && (
            <Text size="sm" c="dimmed">
              {t(store.subscription === 'none' ? 'start.store.none' : 'start.store.unknown')}
            </Text>
          )}
        </>
      ) : (
        <Text size="sm" c="dimmed">
          {t('start.store.missing')}
        </Text>
      )}
    </Tile>
  );
}

function Step({ n, title, children }: { n: number; title: ReactNode; children: ReactNode }) {
  return (
    <Group gap="sm" wrap="nowrap" align="flex-start">
      <ThemeIcon radius="xl" size={28} variant="light" aria-hidden>
        <Text size="sm" fw={700}>
          {n}
        </Text>
      </ThemeIcon>
      <Stack gap={0}>
        <Text size="sm" fw={600}>
          {title}
        </Text>
        <Text size="sm" c="dimmed">
          {children}
        </Text>
      </Stack>
    </Group>
  );
}

/** Die drei Schritte zum Beschreibungscode mit Verweisen. */
function StepsCard({ tenantId }: { tenantId: string }) {
  return (
    <Card withBorder padding="lg">
      <Stack gap="md">
        <Title order={4}>{t('start.steps.title')}</Title>
        <Step
          n={1}
          title={
            <RouterAnchor to="/t/$tenantId/designs" params={{ tenantId }} size="sm" fw={600}>
              {t('nav.designs')}
            </RouterAnchor>
          }
        >
          {t('start.steps.designs')}
        </Step>
        <Step
          n={2}
          title={
            <RouterAnchor to="/t/$tenantId/articles" params={{ tenantId }} size="sm" fw={600}>
              {t('nav.articles')}
            </RouterAnchor>
          }
        >
          {t('start.steps.articles')}
        </Step>
        <Step n={3} title={t('start.steps.copy.title')}>
          {t('start.steps.copy')}
        </Step>
      </Stack>
    </Card>
  );
}

/** Teile, die Kunden noch fehlen (statt der früheren BETA-Hinweise, Flori 01.11.2026); Texte in `start.next.*`. */
const NEXT: readonly TextKey[] = ['start.next.galleries', 'start.next.revise', 'start.next.upload', 'start.next.designExport', 'start.next.account'];

function NextCard() {
  const email = useMe().data?.supportEmail ?? t('account.contact.email');
  const [before, after = ''] = t('start.next.intro', { email: '{email}' }).split('{email}');
  return (
    <Card withBorder padding="lg">
      <Stack gap="md">
        <Group justify="space-between" wrap="nowrap">
          <Title order={4}>{t('start.next.title')}</Title>
          <Badge variant="light" color="gray">
            {t('start.next.badge')}
          </Badge>
        </Group>
        <Stack gap="sm">
          {NEXT.map(key => (
            <Group key={key} gap="sm" wrap="nowrap" align="flex-start">
              <ThemeIcon radius="xl" size={10} color="gray.4" mt={6} aria-hidden>
                {null}
              </ThemeIcon>
              <Stack gap={0}>
                <Text size="sm" fw={600}>
                  {t(key)}
                </Text>
                <Text size="sm" c="dimmed">
                  {t(`${key}.text` as TextKey)}
                </Text>
              </Stack>
            </Group>
          ))}
        </Stack>
        <Text size="xs" c="dimmed">
          {before}
          <Anchor href={`mailto:${email}`} size="xs">
            {email}
          </Anchor>
          {after}
        </Text>
      </Stack>
    </Card>
  );
}

/**
 * eBay-Verbindung mit Live-Update daneben, Angebotszähler und Abgleich (Aufträge); Aktionen darin bleiben eingefroren
 * (`useFrozen`). Ohne eigene Abgleich-Karte (Flori 01.11.2026): die Zeiten stehen in der Auftragstabelle. Fehler- und
 * Warteschlangen-Zähler nur, wo PAT selbst Beschreibungen schreibt (nicht in der BETA).
 */
/** `below`: direkt unter Verbindung und Live-Update, vor Zählern und Abgleich (Angebote updaten, Flori 02.11.2026). */
function EbayInterface({ status, beside, below }: { status: StatusResponse; beside?: ReactNode; below?: ReactNode }) {
  const beta = useMe().data?.memberships.find(m => m.tenantId === status.tenantId)?.beta === true;
  return (
    <>
      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <EbayCard status={status} />
        {beside}
      </SimpleGrid>
      {below}
      <SimpleGrid cols={{ base: 1, sm: beta ? 1 : 3 }} {...(beta ? { maw: 360 } : {})}>
        <CountCard label={t('status.counts.active')} value={status.counts.activeItems} />
        {!beta && <CountCard label={t('status.counts.errors')} value={status.counts.itemsWithReviseError} alert />}
        {!beta && <CountCard label={t('status.counts.pending')} value={status.counts.pendingRevisions} />}
      </SimpleGrid>
      {status.counts.computedAt && (
        <Text size="xs" c="dimmed">
          {t('status.counts.asOf', { date: dateTime(status.counts.computedAt) })}
        </Text>
      )}
      <Group gap="sm">
        <Title order={3}>{t('status.sync')}</Title>
        {status.sync.pauseReason && <Badge color="yellow">{t('status.sync.paused')}</Badge>}
      </Group>
      <SyncNotice status={status} />
      <JobsTable status={status} />
    </>
  );
}

/**
 * Menüpunkt „eBay & Live-Update“ (Flori 01.11.2026, vorher „eBay-Schnittstelle“ und „Live-Update“ getrennt): Verbindung und
 * Live-Update nebeneinander, weil Live-Update ein gültiges Token braucht; darunter Zähler und Abgleich. Auch für BETA-Konten.
 */
export function EbayInterfacePage({ tenantId }: { tenantId: string }) {
  const status = useStatus(tenantId);
  if (status.isPending) return <FullPageLoader />;
  if (status.isError) return <QueryErrorState error={status.error} onRetry={() => void status.refetch()} />;
  return (
    <Stack gap="lg">
      <Title order={2}>{t('ebayInterface.title')}</Title>
      <EbayInterface
        status={status.data}
        beside={<LiveUpdateCard tenantId={tenantId} status={status.data} />}
        below={<LiveUpdateItemCard tenantId={tenantId} status={status.data} />}
      />
      <LiveUpdatePreview tenantId={tenantId} status={status.data} />
    </Stack>
  );
}
